// The "fix this slide's layout" ask, in one place.
//
// Two surfaces raise it and they must word it identically: the Edit tab's
// geometry nudge (Fix in chat) and the red badge on the slide itself (click to
// fix). Three wordings for one problem is how a weak model learns three
// behaviours — the same reasoning that put overflowFixPrompt in actionNotes.js.
//
// This is the USER-FACING half: it lands in the composer as an ordinary
// message, so it reads like something a person would type. The machine-facing
// corrections the studio fires on its own behalf (PREVIEW OVERFLOW: …) stay in
// actionNotes.js behind their sentinels.
//
// It PREFILLS, never auto-sends (Jake, 2026-06-12). The user gets to see what
// is about to be asked, edit it, or think better of it — a warning that silently
// spends a model turn on your behalf is not a fix, it is a surprise.

/**
 * The four things a slide gets flagged for. `overflow` is the fallthrough
 * rather than a tested case, which is deliberate: the badge posts
 * `kind: kind || null`, so an absent kind must still land on the commonest
 * finding rather than on nothing.
 */
export type GeometryFindingKind = 'overflow' | 'overlap' | 'crowding' | 'broken';

export type GeometryFinding = {
  kind?: GeometryFindingKind | null;
  detail?: string | null;
};

/** Plain-English summary of a finding, for the nudge headline, the ask, and the
 *  Activity tab's deck-wide list. One wording for all three. */
export function findingSummary({ kind, detail }: GeometryFinding): string {
  if (kind === 'broken') return `this slide does not render${detail ? ` (${detail})` : ''}`;
  if (kind === 'overlap') return detail ? `elements overlap (${detail})` : 'elements overlap';
  if (kind === 'crowding') return detail || 'elements are jammed together';
  return `content runs past the frame${detail ? ` (${detail})` : ''}`;
}

/**
 * The ask for a geometry finding. Each kind needs a DIFFERENT fix, so the ask
 * names it: content past the frame wants shorter copy, elements painting over
 * each other want room between them (asking to "tighten the copy so it fits"
 * sent the model after the wrong problem), and crowding wants spacing.
 *
 * `summary` may be passed when the caller already has a richer description
 * (the panel names both colliding elements); otherwise it is derived.
 */
export function geometryAsk({ kind, detail, summary }: GeometryFinding & { summary?: string | null }): string {
  const found = summary || findingSummary({ kind, detail });
  // A slide that throws is not a layout problem and must never be sent the
  // layout advice below ("shorten the copy" fixes nothing about a ReferenceError).
  // It reaches here from the Activity tab's deck-wide list, which reports
  // render errors alongside geometry.
  if (kind === 'broken') {
    return `This slide fails to render${detail ? `: ${detail}` : ''}. Find the cause and fix it, keeping the slide's content as it is.`;
  }
  if (kind === 'overlap') {
    return (
      `On this slide, ${found}. Give them room — fewer columns, a smaller size step, or shorter text. ` +
      `Don't squeeze them with CSS or shrink the type below the reading floor.`
    );
  }
  if (kind === 'crowding') {
    return `On this slide, ${found}. Open up the spacing so nothing is jammed against its neighbour or the slide edge.`;
  }
  // Overflow deliberately names the OPTIONS and picks none of them. This used
  // to say "keep the current layout and tighten the copy so it fits", which
  // borrowed the constraint from overflowFixPrompt — right for a proposal
  // mid-review, wrong here. This is the user asking about a slide that is
  // already in the deck, so restructuring and splitting are on the table, and
  // for a slide that is over by hundreds of px they are usually the ONLY
  // answers: six rows that don't fit need five rows or two slides, not six
  // rows in smaller words (Jake, 2026-08-02).
  return (
    `On this slide, ${found}. Fix it whichever way suits the content best: shorten the copy, ` +
    `drop the weakest item, change the layout, or split it across two slides. Don't cut anything that matters.`
  );
}
